import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useAcademic } from '@/components/providers/academic-provider'

import {
    ChartLine,
    ClipboardCheck,
    FileText,
    NotebookPen
} from 'lucide-react'

import {
    DEMO_TODAY,
    formatDate,
    greetingForHour,
    nowInWIB
} from '@/lib/dates'

type HeroActionProps = {
    href: string
    icon: ReactNode
    solid?: boolean
    children: ReactNode
}

const HeroAction: FC<HeroActionProps> = ({ href, icon, solid = false, children }): ReactNode => {
    return (
        <Link
            href={href}
            className={cn(
                'inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-semibold transition-[background-color,transform] outline-none active:translate-y-px focus-visible:ring-3 focus-visible:ring-white/50 [&_svg]:size-4',
                solid
                    ? 'bg-white text-[var(--hero-1)] shadow-sm hover:bg-white/90'
                    : 'bg-white/10 text-white ring-1 ring-white/25 hover:bg-white/20'
            )}
        >
            {icon}
            {children}
        </Link>
    )
}

export const DashboardHero: FC = (): ReactNode => {
    const { currentUser } = useAcademic()
    const [greeting, setGreeting] = useState('Selamat datang')

    // The time of day is only known in the browser, so the greeting settles after mount
    useEffect(() => {
        setGreeting(greetingForHour(nowInWIB().hour))
    }, [])

    return (
        <section aria-labelledby='sapaan' className='relative overflow-hidden rounded-2xl bg-hero px-6 py-7 text-white shadow-float sm:px-8 sm:py-9'>
            <div
                className='absolute inset-0 bg-[linear-gradient(rgb(255_255_255/0.07)_1px,transparent_1px),linear-gradient(90deg,rgb(255_255_255/0.07)_1px,transparent_1px)] [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]'
                style={{ backgroundSize: '28px 28px' }}
                aria-hidden='true'
            />

            <div className='absolute -right-20 -bottom-36 size-80 rounded-full bg-white/10 blur-3xl' aria-hidden='true' />
            <div className='absolute -top-24 right-1/3 size-56 rounded-full bg-cyan-300/10 blur-3xl' aria-hidden='true' />

            <div className='relative max-w-3xl'>
                <span className='inline-flex items-center gap-2 rounded-full bg-white/12 px-3 py-1 text-xs font-semibold ring-1 ring-white/20'>
                    <span className='relative flex size-2' aria-hidden='true'>
                        <span className='absolute inline-flex size-full animate-ping rounded-full bg-emerald-300 opacity-60 motion-reduce:hidden' />
                        <span className='relative inline-flex size-2 rounded-full bg-emerald-300' />
                    </span>

                    {formatDate(DEMO_TODAY, 'full')}
                </span>

                <h1 id='sapaan' className='mt-4 text-[1.65rem] leading-tight font-bold tracking-tight sm:text-[2rem]'>
                    {greeting}, {currentUser.name}!
                </h1>

                <p className='mt-2 max-w-xl text-sm leading-relaxed text-white/85 sm:text-[15px]'>
                    Portal manajemen absensi, penilaian KKM, dan rekapitulasi akademik aktif.
                </p>

                <div className='mt-6 flex flex-wrap gap-2'>
                    <HeroAction href='/absen' icon={<ClipboardCheck aria-hidden='true' />} solid>
                        Catat Absen
                    </HeroAction>

                    <HeroAction href='/nilai' icon={<ChartLine aria-hidden='true' />} solid>
                        Input Nilai KKM
                    </HeroAction>

                    <HeroAction href='/jurnal' icon={<NotebookPen aria-hidden='true' />}>
                        Tulis Jurnal
                    </HeroAction>

                    <HeroAction href='/laporan-harian' icon={<FileText aria-hidden='true' />}>
                        Laporan Harian
                    </HeroAction>
                </div>
            </div>
        </section>
    )
}
